import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const roleCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'Role',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.roles.`,
});

export async function getRoles(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = roleCache.getFromMemory('roles');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('ROLES');
			if (legacyData) {
				log('info', 'Role', '📦 Seeding Vue cache from legacy storage (ROLES)');
				await roleCache.put('roles', legacyData);
				roleCache.setInMemory('roles', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'Role', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	return roleCache.fetchOrCache('roles', fetchRolesFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getRolesMenu(forceRefresh = false) {
	const roleId = iframeDataBridge.getCurrentUser()?.roleSalesId ?? '';
	return roleCache.fetchOrCache(`rolesMenu.${roleId}`, () => fetchRolesMenuFromAPI(roleId), { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getRolePermissions(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = roleCache.getFromMemory('rolePermissions');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('PERMISSIONS');
			if (legacyData) {
				log('info', 'Role', '📦 Seeding Vue cache from legacy storage (PERMISSIONS)');
				await roleCache.put('rolePermissions', legacyData);
				roleCache.setInMemory('rolePermissions', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'Role', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	return roleCache.fetchOrCache('rolePermissions', fetchRolePermissionsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

async function fetchRolesFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Core/Roles'), { method: 'GET' }, 'token');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

	const roles = await response.json();
	log('success', 'Role', `✅ Fetched ${roles.length} roles`);
	return roles;
}

async function fetchRolesMenuFromAPI(roleId) {
	// sidebar.js fetchRolesMenuData(): Token is tokenSales, roleId is ctx.user.roleSalesId.
	const response = await authenticatedFetch(`${getApiUrl('Core/RolesMenu')}?roleId=${encodeURIComponent(roleId)}`, { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

	const rolesMenu = await response.json();
	log('success', 'Role', `✅ Fetched ${rolesMenu.length} roles menu`);
	return rolesMenu;
}

async function fetchRolePermissionsFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Core/RolePermissions'), { method: 'GET' }, 'token');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

	const rolePermissions = await response.json();
	log('success', 'Role', `✅ Fetched ${rolePermissions.length} role permissions`);
	return rolePermissions;
}

export async function refreshRoles() {
	roleCache.clearMemory();
	await roleCache.clear();
}
